import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    Chip,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Add as AddIcon,
    ContentCopy as DuplicateIcon,
    Delete as DeleteIcon,
    Edit as RenameIcon,
    LockOutlined as LockOutlinedIcon,
} from '@mui/icons-material';
import roleAdminService from '../../services/roleAdminService';
import useCan from '../../hooks/useCan';
import { useOptionalPermissions } from '../../context/permissionContextCore';
import NewRoleDialog from './NewRoleDialog';
import FiltroPillole from '../common/FiltroPillole';
import BarraElenco from '../common/BarraElenco';
import { CONTENITORE_TABELLA, TABELLA_PAGINA, TESTATA_TABELLA } from '../common/stiliTabella';

const SPIEGAZIONE_NON_MODIFICABILE =
    'I ruoli di sistema non si modificano: un cambio di ciò che può fare un ruolo predefinito passa da una release.';

const DESCRIZIONE_TAB =
    'Un ruolo raccoglie i permessi che descrivono cosa un utente può fare e su quali dati. Un utente può averne più di uno: i permessi si sommano.';

const NOTA_TABELLA =
    'I ruoli di sistema non si possono eliminare né rinominare, ma si possono duplicare per crearne una variante. Fai clic su una riga per aprire i suoi permessi.';

const FILTRI = [
    { valore: 'tutti', etichetta: 'Tutti' },
    { valore: 'sistema', etichetta: 'Di sistema' },
    { valore: 'personalizzati', etichetta: 'Personalizzati' },
];

/**
 * Tab «Ruoli» della pagina «Ruoli e permessi» (spec §10.2).
 *
 * Stessa forma di `UsersTab`: il caricamento si dichiara e non si mima — una tabella col solo
 * header e' indistinguibile da un elenco vuoto — e un errore di rete si dichiara con un
 * `Alert` e un pulsante «Riprova», non presentandolo come elenco vuoto.
 *
 * Non naviga da se': la rotta del dettaglio la registra un task successivo, e chiama
 * `onOpenRole(roleId)` al clic sulla riga — la stessa forma che `UsersTab` usa gia' con
 * `onSelectUser`.
 *
 * Sui ruoli di sistema l'unica azione e' «Duplica»: il loro contenuto e' generato da uno
 * script e presidiato da un test che legge il changelog, non il database, quindi una
 * modifica da UI li farebbe divergere in silenzio col test verde. Il backend rifiuta con
 * 409, ma mostrare un'azione che il backend rifiuterebbe e' il difetto che questa fase esiste
 * per eliminare.
 */
export default function RolesTab({ onOpenRole }) {
    const can = useCan();
    // Eliminare un ruolo toglie i suoi permessi a chi lo aveva, forse anche a chi elimina.
    const refreshPermissions = useOptionalPermissions()?.refresh;
    // §10.2 protegge la pagina con roles.read; ogni azione ha il suo permesso, e sono tre
    // diversi -- Duplica crea, Rinomina modifica, Elimina cancella. La riga non ne assume
    // nessuno: prima li mostrava tutti a chi ha il solo roles.read, e il 403 arrivava al
    // click. Il gating a livello di azione che `RoleService` dichiara non coperto da
    // `FrontendContractTest` e' ora coperto dai test di questo componente.
    const canCreate = can('roles.create');
    const canUpdate = can('roles.update');
    const canDelete = can('roles.delete');

    const [roles, setRoles] = useState([]);
    const [totalPermissions, setTotalPermissions] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [filter, setFilter] = useState('tutti');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('create');
    const [dialogSource, setDialogSource] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        setError(false);
        // I due dati non si caricano dalla stessa risorsa: il totale dei permessi arriva dal
        // catalogo (§ decisione del task), scritto due volte divergerebbe alla prima
        // risorsa aggiunta.
        Promise.all([roleAdminService.listRoles(), roleAdminService.listPermissionCatalog()])
            .then(([rolesData, catalog]) => {
                setRoles(rolesData || []);
                setTotalPermissions((catalog || []).length);
                setLoading(false);
            })
            .catch(() => {
                setError(true);
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const term = searchTerm.trim().toLowerCase();
    const bySearch = term
        ? roles.filter(
              (role) =>
                  (role.label || '').toLowerCase().includes(term) ||
                  (role.description || '').toLowerCase().includes(term),
          )
        : roles;

    const filteredRoles = useMemo(() => {
        if (filter === 'sistema') {
            return bySearch.filter((r) => r.systemRole);
        }
        if (filter === 'personalizzati') {
            return bySearch.filter((r) => !r.systemRole);
        }
        return bySearch;
    }, [bySearch, filter]);

    const openDuplicateDialog = (role) => {
        setDialogMode('duplicate');
        setDialogSource(role);
        setDialogOpen(true);
    };

    const openRenameDialog = (role) => {
        setDialogMode('rename');
        setDialogSource(role);
        setDialogOpen(true);
    };

    const openNewRoleDialog = () => {
        setDialogMode('create');
        setDialogSource(null);
        setDialogOpen(true);
    };

    const handleDelete = (role) => {
        setDeleteError(null);
        roleAdminService.deleteRole(role.id).then(() => {
            refreshPermissions?.();
            load();
        }, (err) => {
            // Bandiera propria: `error` governa la schermata di caricamento e sostituisce
            // l'intera tabella. Un fallimento di cancellazione non deve far sparire un
            // elenco caricato correttamente, ne' farsi passare per un errore di caricamento
            // — l'utente ha cliccato «Elimina», non «Ricarica».
            const message =
                err?.response?.data?.message ||
                `Non è stato possibile eliminare il ruolo ${role.label}.`;
            setDeleteError(message);
        });
    };

    const handleCreated = () => {
        setDialogOpen(false);
        load();
    };

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={load}>
                            Riprova
                        </Button>
                    }
                >
                    Non è stato possibile caricare i ruoli.
                </Alert>
            </Box>
        );
    }

    return (
        <Box>
            {deleteError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteError(null)}>
                    {deleteError}
                </Alert>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {DESCRIZIONE_TAB}
            </Typography>

            <BarraElenco
                ricerca={searchTerm}
                onRicerca={setSearchTerm}
                placeholderRicerca="Cerca..."
                filtri={
                    <FiltroPillole
                        opzioni={FILTRI}
                        selezione={filter}
                        onCambia={setFilter}
                        ariaLabel="Filtra per tipo"
                    />
                }
                conteggio={
                    loading
                        ? 'Caricamento…'
                        : term || filter !== 'tutti'
                        ? `${filteredRoles.length} di ${roles.length} ${roles.length === 1 ? 'ruolo' : 'ruoli'}`
                        : `${roles.length} ${roles.length === 1 ? 'ruolo' : 'ruoli'}`
                }
                azione={
                    // Nascosto e non disabilitato: un pulsante spento in cima all'elenco e'
                    // rumore permanente, e non c'e' un secondo stato in cui si accende.
                    canCreate ? (
                        <Button variant="contained" startIcon={<AddIcon />} onClick={openNewRoleDialog}>
                            Nuovo ruolo
                        </Button>
                    ) : null
                }
            />

            <TableContainer {...CONTENITORE_TABELLA}>
                <Table size="small" sx={TABELLA_PAGINA}>
                    <TableHead>
                        <TableRow sx={TESTATA_TABELLA}>
                            <TableCell>Ruolo</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell align="right">Utenti</TableCell>
                            <TableCell align="right">Permessi</TableCell>
                            <TableCell>Ultima modifica</TableCell>
                            <TableCell align="right">Azioni</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={7}>
                                    <Typography variant="body2" color="text.secondary">
                                        Caricamento…
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && filteredRoles.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7}>
                                    <Typography variant="body2" color="text.secondary">
                                        Nessun ruolo da mostrare.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading &&
                            filteredRoles.map((role) => (
                                <TableRow
                                    key={role.id}
                                    hover
                                    onClick={() => onOpenRole(role.id)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {role.systemRole && (
                                                // La spiegazione sta sul lucchetto e non piu'
                                                // fra le azioni: con i comandi in riga cio'
                                                // che non si puo' fare non ha un posto dove
                                                // comparire, e il lucchetto e' dove il fatto
                                                // e' gia' detto.
                                                <Tooltip title={SPIEGAZIONE_NON_MODIFICABILE}>
                                                    <LockOutlinedIcon fontSize="small" color="disabled" />
                                                </Tooltip>
                                            )}
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {role.label}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {role.description}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {role.systemRole ? (
                                            <Chip
                                                size="small"
                                                label="Sistema"
                                                variant="filled"
                                                sx={{ bgcolor: 'action.hover' }}
                                            />
                                        ) : (
                                            <Chip
                                                size="small"
                                                label="Personalizzato"
                                                variant="outlined"
                                                color="primary"
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell align="right">{role.userCount}</TableCell>
                                    <TableCell align="right">
                                        {totalPermissions
                                            ? `${role.permissionCount} / ${totalPermissions}`
                                            : role.permissionCount}
                                    </TableCell>
                                    <TableCell>
                                        {role.updatedAt
                                            ? new Date(role.updatedAt).toLocaleDateString('it-IT')
                                            : '—'}
                                    </TableCell>
                                    <TableCell align="right">
                                        {/* I comandi in riga e non dietro un puntino: e' la
                                            forma delle altre sezioni, e sono al massimo tre.
                                            Cio' che un ruolo di sistema non consente qui non
                                            c'e' affatto — la ragione la porta il lucchetto
                                            accanto al nome, che e' dove sta il fatto. */}
                                        <Stack direction="row" spacing={1}
                                               justifyContent="flex-end"
                                               onClick={(event) => event.stopPropagation()}>
                                            {canCreate && (
                                                <IconButton
                                                    size="small"
                                                    title="Duplica"
                                                    aria-label={`duplica ${role.label}`}
                                                    onClick={() => openDuplicateDialog(role)}
                                                >
                                                    <DuplicateIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                            {!role.systemRole && canUpdate && (
                                                <IconButton
                                                    size="small"
                                                    title="Rinomina"
                                                    aria-label={`rinomina ${role.label}`}
                                                    onClick={() => openRenameDialog(role)}
                                                >
                                                    <RenameIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                            {!role.systemRole && canDelete && (
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    title="Elimina"
                                                    aria-label={`elimina ${role.label}`}
                                                    onClick={() => handleDelete(role)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {NOTA_TABELLA}
            </Typography>

            <NewRoleDialog
                open={dialogOpen}
                mode={dialogMode}
                source={dialogSource}
                onClose={() => setDialogOpen(false)}
                onCreated={handleCreated}
            />
        </Box>
    );
}

RolesTab.propTypes = {
    onOpenRole: PropTypes.func.isRequired,
};
